import { MantineProvider } from '@mantine/core';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { theme } from '../theme';

/**
 * A hook that provides driver.js tour functionality with support for:
 * - React content rendering in popovers
 * - actionBefore callbacks before showing steps
 * - Waiting for elements to appear (mutation observers)
 * - Step interaction (clicking on highlighted elements)
 *
 * @param {Object} options
 * @param {Function} options.onComplete - Called when tour completes
 * @param {Function} options.onClose - Called when tour is closed (skip or complete)
 */
const useDriverTour = ({ onComplete, onClose } = {}) => {
  const driverRef = useRef(null);
  const rootsRef = useRef([]);
  const observersRef = useRef({ mutation: null, resize: null });

  // Keep latest callbacks in refs so startTour/closeTour stay stable across renders.
  // Otherwise consumers that put these in useEffect deps will tear the tour down
  // on every parent re-render.
  const onCompleteRef = useRef(onComplete);
  const onCloseRef = useRef(onClose);
  onCompleteRef.current = onComplete;
  onCloseRef.current = onClose;

  // Clean up observers
  const cleanupObservers = useCallback(() => {
    if (observersRef.current.mutation) {
      observersRef.current.mutation.disconnect();
      observersRef.current.mutation = null;
    }
    if (observersRef.current.resize) {
      observersRef.current.resize.disconnect();
      observersRef.current.resize = null;
    }
  }, []);

  // Set up observers to watch for element changes and trigger driver refresh
  const setupElementObservers = useCallback((selector, driverObj) => {
    cleanupObservers();

    const element = document.querySelector(selector);
    if (!element) return;

    // Debounce refresh calls to avoid excessive updates
    let refreshTimeout = null;
    const debouncedRefresh = () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        if (driverObj && driverObj.isActive()) {
          driverObj.refresh();
        }
      }, 300);
    };

    // MutationObserver to watch for DOM changes that might affect element position
    const mutationObserver = new MutationObserver(debouncedRefresh);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    observersRef.current.mutation = mutationObserver;

    // ResizeObserver to watch for element size changes
    const resizeObserver = new ResizeObserver(debouncedRefresh);
    resizeObserver.observe(element);
    observersRef.current.resize = resizeObserver;
  }, [cleanupObservers]);

  // Wait for an element to appear in the DOM
  const waitForElement = useCallback((selector, timeout = 5000) => {
    return new Promise((resolve, reject) => {
      const element = document.querySelector(selector);
      if (element) {
        return resolve(element);
      }

      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          observer.disconnect();
          resolve(el);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Timeout to prevent hanging forever
      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  }, []);

  // Clean up React roots when tour ends
  const cleanupRoots = useCallback(() => {
    rootsRef.current.forEach((root) => {
      try {
        root.unmount();
      } catch (e) {
        // Ignore unmount errors
      }
    });
    rootsRef.current = [];
  }, []);

  /**
   * Start a tour with the given steps
   * @param {Array} steps - Array of step objects with:
   *   - selector: CSS selector for the element to highlight
   *   - content: React element or function returning React element
   *   - title: Optional title string
   *   - actionBefore: Optional function to run before showing this step
   *   - stepInteraction: If true, allows clicking on the highlighted element
   */
  const startTour = useCallback(
    async (steps, { taskId, allowClose = true } = {}) => {
      if (!steps || steps.length === 0) return;

      // Convert steps to driver.js format
      const driverSteps = steps.map((step, index) => ({
        element: step.selector,
        popover: {
          title: step.title || '',
          // Use a placeholder so driver.js creates the description element
          // We'll replace it with React content in onPopoverRender
          description: ' ',
        },
        // Store original step data for use in callbacks
        _originalStep: step,
        _stepIndex: index,
      }));

      // Helper to prepare and move to a step
      const prepareAndMoveToStep = async (stepIndex) => {
        const step = steps[stepIndex];
        if (!step) return;

        // Run actionBefore if defined
        if (step.actionBefore) {
          try {
            await step.actionBefore({
              currentStep: stepIndex,
              setCurrentStep: (idx) => prepareAndMoveToStep(idx),
            });
          } catch (e) {
            console.warn('actionBefore error:', e);
          }
        }

        // Wait for element to appear in DOM (max 3 seconds)
        if (step.selector) {
          try {
            await waitForElement(step.selector, 3000);
            // Small additional delay for element to be fully rendered/positioned
            await new Promise((resolve) => setTimeout(resolve, 50));
          } catch (e) {
            console.warn(`Could not find element: ${step.selector}, proceeding anyway`);
          }
        }

        // Now move to the step
        driverObj.moveTo(stepIndex);

        // Set up observers for the new element
        if (step.selector) {
          setupElementObservers(step.selector, driverObj);
        }
      };

      const driverObj = driver({
        showProgress: true,
        steps: driverSteps,
        nextBtnText: 'Next',
        prevBtnText: 'Previous',
        doneBtnText: 'Done',
        allowClose,
        overlayClickNext: false,
        popoverClass: 'releva-tour-popover',
        onPopoverRender: (popover, { config, state }) => {
          const currentStepIndex = state.activeIndex;
          const step = steps[currentStepIndex];

          // popover.description is the description HTMLElement directly
          const descriptionEl = popover.description;

          if (descriptionEl && step?.content) {
            // Clear existing content
            descriptionEl.innerHTML = '';

            const container = document.createElement('div');
            descriptionEl.appendChild(container);

            const root = createRoot(container);
            rootsRef.current.push(root);

            // If content is a function, call it with step controls
            const content =
              typeof step.content === 'function'
                ? step.content({
                    setCurrentStep: (stepIndex) => {
                      if (stepIndex >= steps.length) {
                        driverObj.destroy();
                      } else {
                        prepareAndMoveToStep(stepIndex);
                      }
                    },
                    currentStep: currentStepIndex,
                    close: () => driverObj.destroy(),
                  })
                : step.content;

            // Use flushSync to ensure React renders immediately
            flushSync(() => {
              root.render(<MantineProvider theme={theme}>{content}</MantineProvider>);
            });
          }

          // Hide title if not provided - popover.title is the title HTMLElement
          if (!step.title && popover.title) {
            popover.title.style.display = 'none';
          }
        },
        onDestroyStarted: () => {
          cleanupObservers();
          cleanupRoots();
          driverObj.destroy();
        },
        onDestroyed: () => {
          cleanupObservers();
          cleanupRoots();
          if (onCloseRef.current) {
            onCloseRef.current(taskId);
          }
        },
        onNextClick: async (element, step, options) => {
          const currentStepIndex = options.state.activeIndex;
          if (currentStepIndex === steps.length - 1) {
            // Last step - complete the tour
            if (onCompleteRef.current) {
              onCompleteRef.current(taskId);
            }
            driverObj.destroy();
          } else {
            // Prepare and move to next step
            await prepareAndMoveToStep(currentStepIndex + 1);
          }
        },
        onPrevClick: async (element, step, options) => {
          const currentStepIndex = options.state.activeIndex;
          if (currentStepIndex > 0) {
            // Prepare and move to previous step
            await prepareAndMoveToStep(currentStepIndex - 1);
          }
        },
      });

      driverRef.current = driverObj;

      // Small delay to ensure DOM is ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Run actionBefore for first step if defined
      const firstStep = steps[0];
      if (firstStep?.actionBefore) {
        try {
          await firstStep.actionBefore({
            currentStep: 0,
            setCurrentStep: (stepIndex) => driverObj.moveTo(stepIndex),
          });
        } catch (e) {
          console.warn('actionBefore error for first step:', e);
        }
      }

      // Wait for first element
      if (firstStep?.selector) {
        try {
          await waitForElement(firstStep.selector);
        } catch (e) {
          console.warn(`Could not find first element: ${firstStep.selector}`);
        }
      }

      driverObj.drive();

      // Set up observers for the first element
      if (firstStep?.selector) {
        setupElementObservers(firstStep.selector, driverObj);
      }
    },
    [waitForElement, cleanupRoots, cleanupObservers, setupElementObservers]
  );

  const closeTour = useCallback(() => {
    cleanupObservers();
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
  }, [cleanupObservers]);

  return {
    startTour,
    closeTour,
  };
};

export default useDriverTour;
